import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { colors } from "@/theme/colors";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // キャンセル RPC の実行中は両ボタンを無効化し、二重送信を防ぐ。
  submitting?: boolean;
};

/**
 * キャンセル実施ボタン押下時の最終確認モーダル。
 * モーダル外クリック・× ボタンのいずれでも閉じられる (JPTP_CANSEI-1269 の表示内容に準拠)。
 * 実行中 (submitting) は閉じる操作・確定操作を無効化し、処理中である旨のメッセージと
 * 確定ボタン内のスピナーを表示する (JPTP_CANSEI-1509)。キャンセル RPC は TableCheck 連携と
 * Stripe Refund を同期で行うため数秒かかることがあり、無反応に見えるのを防ぐ。
 *
 * ボタンは縦積みで 上「キャンセル実施」/ 下「戻る」。Material Design のスタックボタン指針
 * (確定操作を上に置く) に合わせている (JPTP_CANSEI-1470)。
 */
export function CancelConfirmModal({ open, onClose, onConfirm, submitting = false }: Props) {
  const { t } = useTranslation();
  const titleId = "cancel-confirm-modal-title";
  const descriptionId = "cancel-confirm-modal-description";

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      slotProps={{ paper: { sx: { p: 2, width: "100%", maxWidth: 420 } } }}
    >
      <IconButton
        onClick={onClose}
        disabled={submitting}
        aria-label={t("cancelReservation.modalCloseLabel")}
        sx={{ position: "absolute", top: 8, right: 8, color: colors.textMuted }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle id={titleId}>{t("cancelReservation.modalTitle")}</DialogTitle>
      <DialogContent>
        <Typography
          id={descriptionId}
          sx={{ color: colors.textBody, fontSize: 16, whiteSpace: "pre-line" }}
        >
          {t("cancelReservation.modalMessage")}
        </Typography>
        {submitting && (
          <Typography role="status" sx={{ mt: 2, color: colors.textSub, fontSize: 14 }}>
            {t("cancelReservation.modalProcessing")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: "column", gap: 1, px: 3, pb: 1 }}>
        <Button
          variant="contained"
          color="error"
          fullWidth
          onClick={onConfirm}
          disabled={submitting}
          // 実行中はラベルがスピナーに替わるため、アクセシブルネームを aria-label で保つ。
          // スピナー自体は装飾で、処理中の状態は上の role="status" が読み上げる。
          aria-label={t("cancelReservation.modalConfirm")}
        >
          {submitting ? (
            <CircularProgress size={20} aria-hidden />
          ) : (
            t("cancelReservation.modalConfirm")
          )}
        </Button>
        <Button variant="outlined" fullWidth onClick={onClose} disabled={submitting}>
          {t("cancelReservation.modalBack")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
